import { useEffect, useState } from 'react';
import { ClockIcon } from '@heroicons/react/24/outline';
import { useSiteStore } from '../stores/siteStore';
import { getActiveTimezoneInfo } from '../utils/formatDateTime';

/**
 * Live clock that shows the current wall time at the selected site, so the
 * user can tell at a glance whether an alert that fired '15 minutes ago' was
 * during night shift, lunch, etc. — useful when the user is browsing from a
 * different region than the site itself.
 *
 * Updates once per second when seconds are shown, otherwise once per minute,
 * so the cost is negligible.
 */
export default function SiteClock({ showSeconds = false, className = '' }) {
  const { selectedSite } = useSiteStore();
  const { timezone, abbr } = getActiveTimezoneInfo();
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    const intervalMs = showSeconds ? 1000 : 30_000;
    const id = setInterval(() => setNow(new Date()), intervalMs);
    return () => clearInterval(id);
  }, [showSeconds]);

  const timeStr = now.toLocaleTimeString('en-US', {
    timeZone: timezone,
    hour: '2-digit',
    minute: '2-digit',
    ...(showSeconds ? { second: '2-digit' } : {}),
    hour12: true,
  });

  const tooltip = selectedSite?.name
    ? `Current local time at ${selectedSite.name} (${timezone})`
    : `Current time in ${timezone}`;

  return (
    <div
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-slate-50 border border-slate-200 text-sm ${className}`}
      title={tooltip}
    >
      <ClockIcon className="w-3.5 h-3.5 text-slate-500" />
      <span className="font-mono font-medium text-slate-700 tabular-nums">{timeStr}</span>
      <span className="text-xs text-slate-500 font-medium">{abbr}</span>
    </div>
  );
}
